import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Boxes, Download, Loader2, Sparkles, Cpu, Layers, Package, Wand2, Rocket,
  MonitorPlay, Wifi,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KIND_ICON = { character: "🧍", prop: "✨", environment: "🌳" };

export default function Bridge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [enriched, setEnriched] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [engine, setEngine] = useState("unity");
  const [kitPrompt, setKitPrompt] = useState("");
  const [kitRunning, setKitRunning] = useState(false);
  const [kitSteps, setKitSteps] = useState([]);
  const [streamUrl, setStreamUrl] = useState("");
  const [savedStream, setSavedStream] = useState("");
  const [showStream, setShowStream] = useState(false);
  const [savingStream, setSavingStream] = useState(false);

  useEffect(() => {
    api.get(`/bridge/spec/${id}`)
      .then((r) => { setSpec(r.data.build_spec); setEnriched(r.data.enriched); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get(`/bridge/stream/${id}`)
      .then((r) => { if (r.data.url) { setStreamUrl(r.data.url); setSavedStream(r.data.url); } })
      .catch(() => {});
  }, [id]);

  const plan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post(`/bridge/plan/${id}`);
      setSpec(data.build_spec); setEnriched(true);
      toast.success(data.summary || "AAA plan ready! 🎬");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPlanning(false); }
  };

  const triggerDownload = (href, filename) => {
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const exportProject = async () => {
    setExporting(true);
    try {
      const { data } = await api.post(`/bridge/export/${id}`, { engine });
      triggerDownload(`${BACKEND}${data.url}`, data.filename);
      toast.success(`${data.engine === "unreal" ? "Unreal" : "Unity"} project ready — ${data.filename} 📦`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const runKit = async () => {
    setKitRunning(true); setKitSteps([]);
    try {
      const { data } = await api.post(`/bridge/aaa-kit/${id}`, { prompt: kitPrompt || undefined, engine });
      if (data.build_spec) { setSpec(data.build_spec); setEnriched(true); }
      setKitSteps(data.steps || []);
      triggerDownload(`${BACKEND}${data.url}`, data.filename);
      toast.success(data.summary || "AAA kit ready! 🎉");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setKitRunning(false); }
  };

  const saveStream = async () => {
    const u = streamUrl.trim();
    if (!u) return;
    setSavingStream(true);
    try {
      const { data } = await api.post(`/bridge/stream/${id}`, { url: u });
      setSavedStream(data.url); setShowStream(true);
      toast.success("Stream endpoint saved 📡");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStream(false); }
  };

  const clearStream = async () => {
    try {
      await api.delete(`/bridge/stream/${id}`);
      setSavedStream(""); setStreamUrl(""); setShowStream(false);
      toast.success("Stream endpoint removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!spec) {
    return <div className="min-h-[70vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  const w = spec.world || {};
  const counts = { entities: (spec.entities || []).length, assets: (spec.assets || []).length };

  return (
    <div className="max-w-5xl mx-auto p-5 lg:p-8 animate-rise" data-testid="bridge-page">
      <button onClick={() => navigate(`/app/build/${id}`)} className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="bridge-back">
        <ArrowLeft className="w-4 h-4" /> Back to builder
      </button>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] grid place-items-center shrink-0"><Boxes className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Engine Bridge — Export to {engine === "unreal" ? "Unreal" : "Unity"}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Turn “{spec.meta?.title}” into a {engine === "unreal" ? "Unreal" : "Unity"}-ready project. The web app is the brain; the Bridge assembles your scene inside {engine === "unreal" ? "Unreal" : "Unity"}.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--primary))]/35 bg-gradient-to-br from-[hsl(var(--primary))]/12 to-transparent p-5 mb-5" data-testid="aaa-kit-card">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xl">🧊</span>
          <h2 className="font-bold">One-click AAA kit</h2>
          <Badge className="text-[10px]">World + Story + AAA plan + export</Badge>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">From a sentence to a downloadable {engine === "unreal" ? "Unreal" : "Unity"} project in one tap.</p>
        <Input value={kitPrompt} onChange={(e) => setKitPrompt(e.target.value)} className="mb-3"
          placeholder="Optional: describe the world (e.g. a neon cyberpunk rooftop at night)" data-testid="aaa-kit-prompt" />
        <div className="flex flex-wrap items-center gap-3">
          <EngineToggle engine={engine} setEngine={setEngine} />
          <Button onClick={runKit} disabled={kitRunning} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90" data-testid="aaa-kit-btn">
            {kitRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Building your kit…</> : <><Rocket className="w-4 h-4" /> Generate AAA kit (6 credits)</>}
          </Button>
        </div>
        {kitSteps.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="aaa-kit-steps">
            {kitSteps.map((s, i) => <Badge key={i} variant="secondary" className="text-[11px]">{s}</Badge>)}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon={<Layers className="w-4 h-4" />} label="Scene entities" value={counts.entities} testid="bridge-entity-count" />
        <StatCard icon={<Package className="w-4 h-4" />} label="3D asset descriptors" value={counts.assets} testid="bridge-asset-count" />
        <StatCard icon={<Cpu className="w-4 h-4" />} label="Target engine" value={engine === "unreal" ? "Unreal (UE5)" : "Unity (URP)"} />
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge variant="secondary" className="capitalize">{w.biome}</Badge>
          {w.weather && w.weather !== "clear" && <Badge variant="secondary" className="capitalize">{w.weather}</Badge>}
          <Badge variant="secondary" className="capitalize">{w.time_of_day}</Badge>
          {w.terrain && w.terrain !== "flat" && <Badge variant="secondary" className="capitalize">{w.terrain}</Badge>}
          <Badge variant="secondary">{w.size}m world</Badge>
        </div>
        {enriched && spec.art_direction && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">🎨 {spec.art_direction}</p>}
        {enriched && spec.post_fx?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {spec.post_fx.map((fx) => <Badge key={fx} className="capitalize text-[10px]">{fx}</Badge>)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={plan} disabled={planning} className="rounded-full" data-testid="bridge-plan-btn">
            {planning ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting AAA plan…</> : <><Wand2 className="w-4 h-4" /> {enriched ? "Re-run AAA planner" : "Generate AAA plan"} (3 credits)</>}
          </Button>
          <Button onClick={exportProject} disabled={exporting} variant="outline" className="rounded-full" data-testid="bridge-export-btn">
            {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /> Building project…</> : <><Download className="w-4 h-4" /> Download {engine === "unreal" ? "Unreal" : "Unity"} project (.zip)</>}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-5" data-testid="bridge-assets">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold flex items-center gap-2"><Package className="w-4 h-4" /> 3D asset descriptors</h2>
          <Badge variant="outline" className="text-[10px]">Meshy text-to-3D · pending key</Badge>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Each entity uses a primitive placeholder for now. These prompts are ready for the Meshy pipeline — add your key and real <code>.glb</code> models drop straight into the export.</p>
        <div className="space-y-2">
          {(spec.assets || []).map((a) => (
            <div key={a.id} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`bridge-asset-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{KIND_ICON[a.kind] || "📦"}</span>
                <span className="font-semibold text-sm">{a.name}</span>
                <Badge variant="secondary" className="capitalize text-[10px]">{a.kind}</Badge>
                <Badge variant="outline" className="text-[10px] ml-auto capitalize">{a.source}/{a.status}</Badge>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{a.model_prompt}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-5" data-testid="stream-card">
        <h2 className="font-bold flex items-center gap-2 mb-1"><MonitorPlay className="w-4 h-4" /> Live Preview — Pixel Streaming <Badge variant="outline" className="text-[10px]">Phase C</Badge></h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Run your Unreal build + signalling server on your machine or a GPU box (see <code>PixelStreaming/SETUP.md</code> in the Unreal export), then paste its URL to preview the live stream here.</p>
        <div className="flex flex-wrap gap-2">
          <Input value={streamUrl} onChange={(e) => setStreamUrl(e.target.value)} placeholder="https://your-gpu-box:80  (or http://localhost:80)" className="flex-1 min-w-[220px]" data-testid="stream-url-input" />
          <Button onClick={saveStream} disabled={savingStream} className="rounded-full" data-testid="stream-save-btn">
            {savingStream ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />} Save & connect
          </Button>
        </div>
        {savedStream && (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Badge variant="secondary" className="gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Configured</Badge>
              <a href={savedStream} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline" data-testid="stream-open-link">Open stream in new tab ↗</a>
              <button onClick={() => setShowStream((s) => !s)} className="text-[hsl(var(--muted-foreground))] underline" data-testid="stream-toggle-btn">{showStream ? "Hide" : "Show"} embedded preview</button>
              <button onClick={clearStream} className="text-[hsl(var(--destructive))] underline ml-auto" data-testid="stream-clear-btn">Remove</button>
            </div>
            {savedStream.startsWith("http://") && (
              <p className="text-[11px] text-amber-500">⚠️ This page is HTTPS, so an <code>http://</code> stream can't embed (browser mixed-content block). Use “Open in new tab”, or serve the stream over HTTPS to embed it.</p>
            )}
            {showStream && (
              <iframe src={savedStream} title="Pixel Streaming preview" data-testid="stream-iframe"
                className="w-full aspect-video rounded-xl border border-[hsl(var(--border))] bg-black"
                allow="autoplay; fullscreen; gamepad; microphone" />
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-5 text-sm">
        <h3 className="font-bold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> How the Bridge works</h3>
        <ol className="list-decimal list-inside space-y-1 text-[hsl(var(--muted-foreground))]">
          <li>Download the project (Build Spec + auto-generated C# importer + gameplay scripts).</li>
          <li>In Unity (URP): install Newtonsoft Json, copy the <code>Assets/GameView</code> + <code>StreamingAssets</code> folders.</li>
          <li>Run <b>GameView → Import Build Spec</b> (Unity) — a playable scene is assembled (player, collectibles, enemies, NPCs, world). Press Play. For <b>Unreal</b>, switch the engine toggle above for a UE5 plugin + Python importer.</li>
          <li>Add your <b>Meshy</b> key in GameView to swap primitives for real AI-generated 3D models. Pixel Streaming (browser preview of the engine build) comes in Phase C — see <code>PixelStreaming/SETUP.md</code> in the Unreal export.</li>
        </ol>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, testid }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] text-xs font-semibold mb-1">{icon} {label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

const EngineToggle = ({ engine, setEngine }) => (
  <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-0.5" data-testid="engine-toggle">
    {[["unity", "Unity"], ["unreal", "Unreal"]].map(([v, l]) => (
      <button key={v} onClick={() => setEngine(v)} data-testid={`engine-${v}`}
        className={`px-3.5 py-1 rounded-full text-xs font-semibold transition-colors ${engine === v ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>{l}</button>
    ))}
  </div>
);
